import {
  retrieveStudioItemFeedbacks,
  type RetrieveStudioItemFeedbacksParams,
} from '@/entities/studio';
import { v1ApiClient } from '@/services';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type { HeySynclyStudioItem, HeySynclyStudioListItem } from '../model';
import type {
  CreateHeySynclyStudioItemRequestParams,
  RetrieveHeySynclyStudioItemsRequestParams,
} from './dto';

export const studioService = {
  CreateHeySynclyStudioItem: async (
    params: CreateHeySynclyStudioItemRequestParams,
  ): Promise<HeySynclyStudioItem> => {
    const workspace_id = getCurrWorkspaceAlias();
    const response = await v1ApiClient.post(
      `/workspaces/${workspace_id}/hey-syncly/studio/items`,
      params,
    );
    return response.data;
  },
  GetHeySynclyStudioItem: async (
    item_id: Alias,
  ): Promise<HeySynclyStudioItem> => {
    const response = await v1ApiClient.get(
      `/hey-syncly/studio/items/${item_id}`,
    );
    return response.data;
  },
  GetHeySynclyStudioItemPublic: async (
    item_id: Alias,
  ): Promise<HeySynclyStudioItem> => {
    const response = await v1ApiClient.get(
      `/hey-syncly/studio/items/${item_id}/public`,
    );
    return response.data;
  },
  RetrieveHeySynclyStudioItems: async (
    params: RetrieveHeySynclyStudioItemsRequestParams,
  ): Promise<{
    items: HeySynclyStudioListItem[];
    total_count: number;
  }> => {
    const workspace_id = getCurrWorkspaceAlias();
    const queryParams = new URLSearchParams();
    queryParams.append('limit', params.limit.toString());
    queryParams.append('offset', params.offset.toString());
    params.type?.forEach((type) => {
      queryParams.append('type', type);
    });
    if (params.search) {
      queryParams.append('search', params.search);
    }
    const response = await v1ApiClient.get(
      `/workspaces/${workspace_id}/hey-syncly/studio/items`,
      {
        params: queryParams,
      },
    );
    return response.data;
  },
  SaveHeySynclyStudioItemFromChat: async (
    item_id: Alias,
  ): Promise<HeySynclyStudioItem> => {
    const response = await v1ApiClient.post(
      `/hey-syncly/studio/items/${item_id}`,
    );
    return response.data;
  },
  VoteHeySynclyStudioItem: async (
    item_id: Alias,
    vote_up?: boolean,
  ): Promise<HeySynclyStudioItem> => {
    const response = await v1ApiClient.patch(
      `/hey-syncly/studio/items/${item_id}/votes`,
      { vote_up },
    );
    return response.data;
  },
  RetrieveHeySynclyStudioItemFeedbacks: (
    item_id: Alias,
    params: RetrieveStudioItemFeedbacksParams,
  ) => retrieveStudioItemFeedbacks(item_id, params),
  ExportCategoryMentionList: async (
    item_id: Alias,
    params: {
      category_id: number;
      reason?: string;
      password?: string;
    },
  ): Promise<void> => {
    const { category_id, reason, password } = params;
    const response = await v1ApiClient.post(
      `/hey-syncly/studio/items/${item_id}/feedbacks/export`,
      {
        reason,
        password,
        category_id,
      },
    );
    return response.data;
  },
};
